import React, { useEffect, useRef } from 'react';

/**
 * スクリーンリーダー専用のテキストを表示するコンポーネント
 */
export const ScreenReaderOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <span className="sr-only">
      {children}
    </span>
  );
};

/**
 * ライブリージョンコンポーネント
 * スクリーンリーダーに変更を通知
 */
interface LiveRegionProps {
  message: string;
  politeness?: 'polite' | 'assertive' | 'off';
  atomic?: boolean;
  relevant?: 'additions' | 'removals' | 'text' | 'all';
}

export const LiveRegion: React.FC<LiveRegionProps> = ({
  message,
  politeness = 'polite',
  atomic = true,
  relevant = 'additions',
}) => {
  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic={atomic}
      aria-relevant={relevant}
      className="sr-only"
    >
      {message}
    </div>
  );
};

/**
 * フォーカストラップコンポーネント
 * モーダルやドロップダウン内でフォーカスを管理
 */
interface FocusTrapProps {
  children: React.ReactNode;
  active?: boolean;
  returnFocus?: boolean;
}

export const FocusTrap: React.FC<FocusTrapProps> = ({
  children,
  active = true,
  returnFocus = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;

    // 現在のフォーカス要素を保存
    previousFocusRef.current = document.activeElement as HTMLElement;

    const container = containerRef.current;
    if (!container) return;

    // フォーカス可能な要素を取得
    const getFocusableElements = () => {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ];
      return container.querySelectorAll<HTMLElement>(focusableSelectors.join(','));
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift + Tab
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      }
      // Tab
      else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    // 最初の要素にフォーカス
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      
      // フォーカスを元に戻す
      if (returnFocus && previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [active, returnFocus]);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
};

/**
 * スキップリンクコンポーネント
 * キーボードユーザーがメインコンテンツに素早くアクセスできるように
 */
export const SkipLink: React.FC<{ href?: string; label?: string }> = ({
  href = '#main-content',
  label = 'メインコンテンツへスキップ',
}) => {
  return (
    <a
      href={href}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-500 focus:text-white focus:rounded"
    >
      {label}
    </a>
  );
};

/**
 * アクセシブルなアイコンボタンコンポーネント
 */
interface AccessibleIconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  icon: React.ReactNode;
}

export const AccessibleIconButton: React.FC<AccessibleIconButtonProps> = ({
  label,
  icon,
  ...props
}) => {
  return (
    <button
      aria-label={label}
      {...props}
    >
      {icon}
      <ScreenReaderOnly>{label}</ScreenReaderOnly>
    </button>
  );
};

/**
 * アクセシブルなフォームフィールドコンポーネント
 */
interface AccessibleFormFieldProps {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  description?: string;
  children: React.ReactElement;
}

export const AccessibleFormField: React.FC<AccessibleFormFieldProps> = ({
  id,
  label,
  error,
  required,
  description,
  children,
}) => {
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const ariaDescribedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1" aria-label="必須">*</span>}
      </label>
      
      {description && (
        <p id={descriptionId} className="text-sm text-gray-500 mb-1">
          {description}
        </p>
      )}
      
      {React.cloneElement(children, {
        id,
        'aria-invalid': !!error,
        'aria-describedby': ariaDescribedBy,
        'aria-required': required,
      })}
      
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * キーボードナビゲーション用のカスタムフック
 */
export function useKeyboardNavigation(
  itemsCount: number,
  onSelect: (index: number) => void,
  options?: {
    vertical?: boolean;
    loop?: boolean;
    onEscape?: () => void;
  }
) {
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const { vertical = true, loop = true, onEscape } = options || {};

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          if (vertical ? e.key === 'ArrowDown' : e.key === 'ArrowRight') {
            e.preventDefault();
            setFocusedIndex((prev) => {
              const next = prev + 1;
              if (next >= itemsCount) {
                return loop ? 0 : prev;
              }
              return next;
            });
          }
          break;
          
        case 'ArrowUp':
        case 'ArrowLeft':
          if (vertical ? e.key === 'ArrowUp' : e.key === 'ArrowLeft') {
            e.preventDefault();
            setFocusedIndex((prev) => {
              const next = prev - 1;
              if (next < 0) {
                return loop ? itemsCount - 1 : prev;
              }
              return next;
            });
          }
          break;
          
        case 'Enter':
        case ' ':
          e.preventDefault();
          onSelect(focusedIndex);
          break;
          
        case 'Escape':
          if (onEscape) {
            e.preventDefault();
            onEscape();
          }
          break;
          
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
          
        case 'End':
          e.preventDefault();
          setFocusedIndex(itemsCount - 1);
          break;
      }
    },
    [focusedIndex, itemsCount, loop, vertical, onSelect, onEscape]
  );

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
  };
}

/**
 * ARIAアナウンスメント用のフック
 */
export function useAnnouncement() {
  const [announcement, setAnnouncement] = React.useState('');
  
  const announce = React.useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    setAnnouncement('');
    setTimeout(() => {
      setAnnouncement(message);
    }, 100);
  }, []);

  return {
    announcement,
    announce,
  };
}
